import { useMemo, useState } from 'react';
import { CellType } from '@grapecity/wijmo.grid';
import CustomGrid from 'components/grids/CustomGrid';
import { useUtComparisonElecStore } from '../store/useUtComparisonElecStore';
import { ElecSourceRow } from '../types';

/** 설비군별 표의 행 종류를 담는 키 (컬럼으로는 쓰지 않는다) */
const GRP_KIND_KEY = '__kind';

/** 행 종류별 배경/글자색 — 예전 tr.grp-new / grp-del / grp-total 클래스와 같은 값 */
const GRP_ROW_BG: Record<string, string> = {
  new: '#dcfbea', // Confirmed-100 (비교대상에만 있는 신규 설비군)
  del: '#fef1eb', // Error-100 (기준에만 있고 사라진 설비군)
  total: '#f1f4f3', // Grey-100 (합계)
};
const GRP_ROW_COLOR: Record<string, string> = {
  new: '#01463a',
  del: '#b32542',
};

/** 설비군별 표 본문 높이 (예전 .grp-tbl-wrap의 max-height와 같은 값) */
const GRP_GRID_HEIGHT = 320;

/** 전압유형/설비군 카드·표 배색 팔레트 (ELM 팔레트 순환 — Lightblue/Secondary/Success/Warning/Error/Primary) */
const PALETTE = [
  { bg: '#e3f0ff', border: '#adceff', text: '#3b5db7' },
  { bg: '#e6def9', border: '#a897df', text: '#554596' },
  { bg: '#dcfbea', border: '#56d8aa', text: '#01463a' },
  { bg: '#fffbe5', border: '#ffe56a', text: '#7a5a00' },
  { bg: '#fef1eb', border: '#fda293', text: '#b32542' },
  { bg: '#ecfcf8', border: '#8bf0e9', text: '#135678' },
];

const fmt1 = (v: number) => v.toFixed(1);
const delta = (b: number, a: number, digits = 1) => {
  const d = a - b;
  const cls = d > 0 ? 'delta-pos' : d < 0 ? 'delta-neg' : 'delta-zero';
  const sign = d > 0 ? '+' : '';
  return (
    <span className={`delta ${cls}`}>
      ({sign}
      {d.toFixed(digits)})
    </span>
  );
};

const sumBy = (rows: ElecSourceRow[], pred: (r: ElecSourceRow) => boolean) =>
  rows.filter(pred).reduce(
    (acc, r) => ({
      qty: acc.qty + r.elpwEqpQty,
      cap: acc.cap + (r.elpwSumCapa ?? 0),
      dmd: acc.dmd + (r.dmdElpwCapa ?? 0),
    }),
    { qty: 0, cap: 0, dmd: 0 }
  );

/**
 * 전압 계통별 Capacity 비교(전압별/설비군별 탭) + 전체 합계.
 *
 * 다른 영역과 달리 섹션 제목·접기를 두지 않는다(SectionBox 미사용) —
 * 화면을 열자마자 눈에 들어와야 하는 지표라 상시 노출한다.
 */
const VoltGroupPanel = () => {
  const sourceBefore = useUtComparisonElecStore((s) => s.sourceBefore);
  const sourceAfter = useUtComparisonElecStore((s) => s.sourceAfter);
  // 기본은 설비군별 — 전압별보다 먼저 보는 화면이라 초기 탭으로 둔다
  const [tab, setTab] = useState<'volt' | 'grp'>('grp');

  const allVts = useMemo(
    () =>
      Array.from(new Set([...sourceBefore, ...sourceAfter].map((r) => r.vt)))
        .filter((v) => v && v !== 'UNKNOWN')
        .sort((a, b) => {
          const na = parseFloat(a);
          const nb = parseFloat(b);
          if (na !== nb) return na - nb;
          return a.localeCompare(b);
        }),
    [sourceBefore, sourceAfter]
  );

  const allGrps = useMemo(
    () =>
      Array.from(new Set([...sourceBefore, ...sourceAfter].map((r) => r.eqclNm)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, 'ko')),
    [sourceBefore, sourceAfter]
  );

  const total = useMemo(() => {
    const tb = sumBy(sourceBefore, () => true);
    const ta = sumBy(sourceAfter, () => true);
    return { tb, ta };
  }, [sourceBefore, sourceAfter]);

  /** 설비군별 표 컬럼 — 전압유형이 데이터마다 달라서 동적으로 만든다 */
  const grpLayout = useMemo(() => {
    /*
      설비군 열은 '*'(남는 폭 차지)로 둔다 — 예전 표가 width:100%라 전압유형이 적으면
      패널을 꽉 채웠는데, 고정 px만 쓰면 오른쪽에 빈 공간이 생긴다.
      전압유형이 많아 합계 폭이 패널을 넘으면 그때부터 그리드가 가로로 스크롤한다.
    */
    const layout: any[] = [
      { binding: 'eqclNm', header: '설비군', minWidth: 110, width: '*' },
      { binding: 'qtyBefore', header: "Q'ty 전", minWidth: 60, width: 80, align: 'right' },
      { binding: 'qtyAfter', header: "Q'ty 후", minWidth: 60, width: 80, align: 'right' },
    ];
    allVts.forEach((vt, i) => {
      layout.push({
        header: vt,
        columns: [
          { binding: `v${i}_b`, header: '수용율(kW) 전', minWidth: 80, width: 105, align: 'right' },
          { binding: `v${i}_a`, header: '수용율(kW) 후', minWidth: 80, width: 115, align: 'right' },
        ],
      });
    });
    return layout;
  }, [allVts]);

  /** 설비군 한 줄 + 마지막 합계 줄 (값은 숫자로 두고 표시 문자열은 itemFormatter가 만든다) */
  const grpRowData = useMemo(() => {
    /** grp이 null이면 전체 합계 */
    const capOf = (rows: ElecSourceRow[], grp: string | null, vt: string) =>
      rows
        .filter((r) => (grp === null || r.eqclNm === grp) && r.vt === vt)
        .reduce((s, r) => s + (r.dmdElpwCapa ?? 0), 0);

    const items = allGrps.map((grp) => {
      const qb = sourceBefore.filter((r) => r.eqclNm === grp).reduce((s, r) => s + r.elpwEqpQty, 0);
      const qa = sourceAfter.filter((r) => r.eqclNm === grp).reduce((s, r) => s + r.elpwEqpQty, 0);
      const item: Record<string, any> = {
        eqclNm: grp,
        qtyBefore: qb,
        qtyAfter: qa,
        [GRP_KIND_KEY]: qb === 0 ? 'new' : qa === 0 ? 'del' : '',
      };
      allVts.forEach((vt, i) => {
        item[`v${i}_b`] = capOf(sourceBefore, grp, vt);
        item[`v${i}_a`] = capOf(sourceAfter, grp, vt);
      });
      return item;
    });

    const totalItem: Record<string, any> = {
      eqclNm: 'Total',
      qtyBefore: total.tb.qty,
      qtyAfter: total.ta.qty,
      [GRP_KIND_KEY]: 'total',
    };
    allVts.forEach((vt, i) => {
      totalItem[`v${i}_b`] = capOf(sourceBefore, null, vt);
      totalItem[`v${i}_a`] = capOf(sourceAfter, null, vt);
    });

    return [...items, totalItem];
  }, [allGrps, allVts, sourceBefore, sourceAfter, total]);

  const grpItemFormatter = (panel: any, r: number, c: number, cell: HTMLElement) => {
    if (CellType.Cell !== panel.cellType) return;
    const item = panel.rows[r]?.dataItem;
    if (!item) return;

    // 셀 엘리먼트가 재사용되므로 매번 초기화한다
    cell.style.background = '';
    cell.style.color = '';
    cell.style.fontWeight = '';

    const kind = item[GRP_KIND_KEY];
    if (GRP_ROW_BG[kind]) cell.style.background = GRP_ROW_BG[kind];
    if (GRP_ROW_COLOR[kind]) cell.style.color = GRP_ROW_COLOR[kind];
    if (kind === 'total') cell.style.fontWeight = '700';

    const binding = String(panel.columns[c].binding || '');
    if (binding === 'eqclNm') return;

    const isTotal = kind === 'total';
    const v = Number(item[binding] ?? 0);

    // Q'ty는 정수, 값이 없으면 '-' (합계 줄은 0도 그대로 보여준다)
    if (binding === 'qtyBefore' || binding === 'qtyAfter') {
      cell.textContent = isTotal || v ? String(v) : '-';
      return;
    }

    const m = /^v(\d+)_(b|a)$/.exec(binding);
    if (!m) return;
    cell.textContent = isTotal || v > 0 ? fmt1(v) : '-';

    // '후' 칸에만 증감을 덧붙인다 (합계 줄은 붙이지 않던 기존 동작 유지)
    if (isTotal || m[2] !== 'a' || v <= 0) return;
    const d = v - Number(item[`v${m[1]}_b`] ?? 0);
    if (d === 0) return;
    const span = document.createElement('span');
    span.className = `delta ${d > 0 ? 'delta-pos' : 'delta-neg'}`;
    span.textContent = ` (${d > 0 ? '+' : ''}${d.toFixed(1)})`;
    cell.appendChild(span);
  };

  if (sourceBefore.length === 0 && sourceAfter.length === 0) return null;

  return (
    <div className="top-panels">
      <div className="panel-box">
        <div className="panel-title">
          전압 계통별 Capacity 비교
          <div className="volt-tabs">
            <button
              type="button"
              className={`vtab${tab === 'grp' ? ' active' : ''}`}
              onClick={() => setTab('grp')}
            >
              설비군별
            </button>
            <button
              type="button"
              className={`vtab${tab === 'volt' ? ' active' : ''}`}
              onClick={() => setTab('volt')}
            >
              전압별
            </button>
          </div>
        </div>

        {tab === 'volt' && (
          <div className="volt-grid">
            {allVts.map((vt, i) => {
              const c = PALETTE[i % PALETTE.length];
              const b = sumBy(sourceBefore, (r) => r.vt === vt);
              const a = sumBy(sourceAfter, (r) => r.vt === vt);
              return (
                <div
                  key={vt}
                  className="vcell"
                  style={{ background: c.bg, border: `1px solid ${c.border}` }}
                >
                  <div className="vt" style={{ color: c.text }}>
                    {vt}
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <td>Q&apos;ty</td>
                        <td>
                          {b.qty} → {a.qty} {delta(b.qty, a.qty, 0)}
                        </td>
                      </tr>
                      <tr>
                        <td>Capacity</td>
                        <td>
                          {fmt1(b.cap)} → {fmt1(a.cap)} kW
                        </td>
                      </tr>
                      <tr>
                        <td>수용율 용량(kW)</td>
                        <td>
                          {fmt1(b.dmd)} → {fmt1(a.dmd)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'grp' && (
          /* 헤더 글자를 작게 — sharedStyles.ts의 .grid-sm-header 참고 */
          <div className="grid-sm-header">
            <CustomGrid
              id="utElecVoltGroupGrid"
              layoutDefinition={grpLayout}
              rowData={grpRowData}
              height={GRP_GRID_HEIGHT}
              /* 설비군을 고정해 전압유형이 많아도 어느 행인지 알 수 있게 한다 */
              frozenColumns={1}
              itemFormatter={grpItemFormatter}
              /* 마지막 합계 줄이 중간으로 끼어들지 않도록 정렬은 막는다 */
              allowSorting={false}
              isFilter={false}
              isSelector={false}
              isReadOnly={true}
              allowPinning={false}
            />
          </div>
        )}
      </div>

      <div className="panel-box">
        <div className="panel-title">전체 합계</div>
        <div className="tot-row">
          <span className="tot-label">Q&apos;ty (전)</span>
          <span className="tot-val">{total.tb.qty.toLocaleString()}</span>
        </div>
        <div className="tot-row">
          <span className="tot-label">Q&apos;ty (후)</span>
          <span className="tot-val">
            {total.ta.qty.toLocaleString()} {delta(total.tb.qty, total.ta.qty, 0)}
          </span>
        </div>
        <div className="tot-row">
          <span className="tot-label">Capacity 합계 (전)</span>
          <span className="tot-val">{fmt1(total.tb.cap)} kW</span>
        </div>
        <div className="tot-row">
          <span className="tot-label">Capacity 합계 (후)</span>
          <span className="tot-val">
            {fmt1(total.ta.cap)} kW {delta(total.tb.cap, total.ta.cap)}
          </span>
        </div>
        <div className="tot-row">
          <span className="tot-label">수용율 용량(kW) 합계 (전)</span>
          <span className="tot-val">{fmt1(total.tb.dmd)} kW</span>
        </div>
        <div className="tot-row">
          <span className="tot-label">수용율 용량(kW) 합계 (후)</span>
          <span className="tot-val">
            {fmt1(total.ta.dmd)} kW {delta(total.tb.dmd, total.ta.dmd)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default VoltGroupPanel;
